// src/components/common/confirmDelete.js
import React from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { GlobalStyles } from "../../constants/styles";
import { alphaFromHex } from "../../util/colors";

import en from "../../i18n/translations/en";
import es from "../../i18n/translations/es";
import fr from "../../i18n/translations/fr";

const dictionaries = { en, es, fr };
const supportedLanguages = ["en", "es", "fr"];

function getCurrentLanguage() {
    if (typeof window === "undefined") return "en";

    const saved = localStorage.getItem("language");
    if (saved && supportedLanguages.includes(saved)) return saved;

    const browserLang = navigator.language?.slice(0, 2)?.toLowerCase();
    return supportedLanguages.includes(browserLang) ? browserLang : "en";
}

function getNestedValue(obj, path) {
    return path.split(".").reduce((acc, key) => acc?.[key], obj);
}

function interpolate(template, vars = {}) {
    if (typeof template !== "string") return template;

    return Object.entries(vars).reduce((result, [key, value]) => {
        return result.replaceAll(`{${key}}`, value ?? "");
    }, template);
}

function t(key, vars = {}, fallback = "") {
    const lang = getCurrentLanguage();
    const dictionary = dictionaries[lang] || dictionaries.en;
    const value = getNestedValue(dictionary, key);

    if (value == null) return fallback || key;
    return interpolate(value, vars);
}

function cleanupConfirmAlertArtifacts() {
    document.querySelectorAll(".react-confirm-alert-overlay").forEach((el) => el.remove());

    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("overflow");
    document.body.style.removeProperty("padding-right");

    document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
}

function getBrandedPrimaryColor() {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return (
            GlobalStyles.colors.primary ||
            GlobalStyles.colors.canary ||
            GlobalStyles.colors.woodsmoke ||
            "#00adff"
        );
    }

    const root = document.documentElement;

    let val =
        root.style.getPropertyValue("--kbs-primary-color") ||
        root.style.getPropertyValue("--kbs-primary");

    if (!val) {
        const cs = getComputedStyle(root);
        val =
            cs.getPropertyValue("--kbs-primary-color") ||
            cs.getPropertyValue("--kbs-primary");
    }

    val = (val || "").trim();

    if (val) return val;

    return (
        GlobalStyles.colors.primary ||
        GlobalStyles.colors.canary ||
        GlobalStyles.colors.woodsmoke ||
        "#00adff"
    );
}

function getBrandedTextOnPrimary() {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return GlobalStyles.colors.woodsmoke || "#14151a";
    }

    const root = document.documentElement;

    let val =
        root.style.getPropertyValue("--kbs-text-on-primary") ||
        root.style.getPropertyValue("--kbs-primary-contrast");

    if (!val) {
        const cs = getComputedStyle(root);
        val =
            cs.getPropertyValue("--kbs-text-on-primary") ||
            cs.getPropertyValue("--kbs-primary-contrast");
    }

    val = (val || "").trim();

    if (val) return val;

    return GlobalStyles.colors.woodsmoke || "#14151a";
}

function ConfirmDeleteUI({
                             onClose,
                             title,
                             message,
                             confirmLabel,
                             cancelLabel,
                             danger,
                             busy,
                             onConfirm,
                             badgeLabel,
                             closeLabel,
                             hintText,
                             workingLabel,
                         }) {
    const [isClosing, setIsClosing] = React.useState(false);

    const primary = getBrandedPrimaryColor();
    const textOnPrimary = getBrandedTextOnPrimary();

    const styles = {
        overlay: {
            zIndex: 2000,
            background: "rgba(0,0,0,0.45)",
            backdropFilter: "blur(2px)",
        },
        modal: {
            backgroundColor: "#fff",
            color: "#14151a",
            borderRadius: 16,
            maxWidth: 440,
            width: "92%",
            border: "1px solid rgba(0,0,0,0.08)",
            boxShadow: "0 18px 44px rgba(0,0,0,0.28)",
            fontFamily: "'Poppins', sans-serif",
            overflow: "hidden",
        },
        header: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 14px",
            background: `linear-gradient(
                135deg,
                ${alphaFromHex(primary, 0.50)},
                ${alphaFromHex(primary, 0.00)}
              )`,
            borderBottom: "1px solid rgba(0,0,0,0.06)",
        },
        titleWrap: {
            display: "flex",
            alignItems: "center",
            gap: 10,
        },
        badge: {
            fontSize: 12,
            fontWeight: 900,
            letterSpacing: "0.04em",
            padding: "6px 10px",
            borderRadius: 999,
            background: danger ? "rgba(255, 77, 79, 0.14)" : primary,
            border: danger
                ? "1px solid rgba(255, 77, 79, 0.28)"
                : "1px solid rgba(0,0,0,0.10)",
            color: danger ? "rgba(160, 25, 25, 0.95)" : textOnPrimary,
        },
        title: {
            margin: 0,
            fontSize: 16,
            fontWeight: 900,
            color: "#14151a",
        },
        closeBtn: {
            width: 36,
            height: 36,
            borderRadius: 10,
            border: "1px solid rgba(0,0,0,0.10)",
            background: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
        },
        body: {
            padding: 14,
            background: "#fff",
        },
        message: {
            fontSize: 13.5,
            lineHeight: 1.35,
            color: "rgba(20,21,26,0.80)",
            whiteSpace: "pre-line",
            margin: 0,
        },
        hint: {
            marginTop: 10,
            fontSize: 12.5,
            fontWeight: 600,
            color: "rgba(20,21,26,0.55)",
        },
        footer: {
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            padding: "12px 14px",
            background: "rgba(0,0,0,0.02)",
            borderTop: "1px solid rgba(0,0,0,0.06)",
            gap: 10,
        },
        btn: {
            borderRadius: 12,
            padding: "10px 12px",
            fontWeight: 900,
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            minWidth: 110,
            justifyContent: "center",
            border: "1px solid rgba(0,0,0,0.12)",
        },
        btnGhost: {
            background: "#fff",
            color: "#14151a",
        },
        btnDanger: {
            background: GlobalStyles.colors.lose || "#dc3545",
            border: "1px solid rgba(0,0,0,0.10)",
            color: "#fff",
        },
        btnPrimary: {
            background: GlobalStyles.colors.canary,
            border: "1px solid rgba(0,0,0,0.10)",
            color: GlobalStyles.colors.woodsmoke,
        },
        btnDisabled: {
            opacity: 0.7,
            cursor: "not-allowed",
        },
    };

    const safeClose = () => {
        if (isClosing) return;
        setIsClosing(true);

        try {
            onClose();
        } finally {
            setTimeout(() => cleanupConfirmAlertArtifacts(), 0);
        }
    };

    const handleConfirmClick = async () => {
        if (busy || isClosing) return;

        try {
            await onConfirm();
        } finally {
            safeClose();
        }
    };

    return (
        <div className="react-confirm-alert-overlay" style={styles.overlay}>
            <div className="react-confirm-alert" style={styles.modal}>
                <div style={styles.header}>
                    <div style={styles.titleWrap}>
                        <span style={styles.badge}>{badgeLabel}</span>
                        <h4 style={styles.title}>{title}</h4>
                    </div>

                    <button
                        type="button"
                        onClick={safeClose}
                        aria-label={closeLabel}
                        title={closeLabel}
                        style={{
                            ...styles.closeBtn,
                            ...(busy ? styles.btnDisabled : null),
                        }}
                        disabled={busy}
                    >
                        <span aria-hidden="true" style={{ fontSize: 20, lineHeight: 1 }}>
                            &times;
                        </span>
                    </button>
                </div>

                <div style={styles.body}>
                    <p style={styles.message}>{message}</p>
                    <div style={styles.hint}>{hintText}</div>
                </div>

                <div style={styles.footer}>
                    <button
                        type="button"
                        className="btn"
                        onClick={safeClose}
                        style={{
                            ...styles.btn,
                            ...styles.btnGhost,
                            ...(busy ? styles.btnDisabled : null),
                        }}
                        disabled={busy}
                    >
                        <i className="fas fa-times" />
                        {cancelLabel}
                    </button>

                    <button
                        type="button"
                        className="btn"
                        onClick={handleConfirmClick}
                        style={{
                            ...styles.btn,
                            ...(danger ? styles.btnDanger : styles.btnPrimary),
                            ...(busy ? styles.btnDisabled : null),
                        }}
                        disabled={busy}
                    >
                        <i className={`fas ${danger ? "fa-trash" : "fa-check"}`} />
                        {busy ? workingLabel : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

export function confirmDelete({
                                  title,
                                  message,
                                  confirmLabel,
                                  cancelLabel,
                                  danger = true,
                                  busy = false,
                                  onConfirm = async () => {},
                                  badgeLabel,
                                  closeLabel,
                                  hintText,
                                  workingLabel,
                              }) {
    const resolvedTitle =
        title ?? t("confirmDelete.defaults.title", {}, "Are you sure?");

    const resolvedMessage =
        message ?? t("confirmDelete.defaults.message", {}, "This action cannot be undone.");

    const resolvedConfirmLabel =
        confirmLabel ??
        (danger
            ? t("confirmDelete.buttons.delete", {}, "Delete")
            : t("confirmDelete.buttons.confirm", {}, "Confirm"));

    const resolvedCancelLabel =
        cancelLabel ?? t("confirmDelete.buttons.cancel", {}, "Cancel");

    const resolvedBadgeLabel =
        badgeLabel ??
        (danger
            ? t("confirmDelete.badge.danger", {}, "DANGER")
            : t("confirmDelete.badge.confirm", {}, "CONFIRM"));

    const resolvedCloseLabel =
        closeLabel ?? t("confirmDelete.aria.close", {}, "Close");

    const resolvedHintText =
        hintText ??
        (danger
            ? t(
                "confirmDelete.hints.danger",
                {},
                "This action is destructive and may affect related data."
            )
            : t("confirmDelete.hints.confirm", {}, "Please confirm to continue."));

    const resolvedWorkingLabel =
        workingLabel ?? t("confirmDelete.buttons.working", {}, "Working...");

    confirmAlert({
        overlayClassName: "kbola-confirm-alert-overlay",
        closeOnEscape: true,
        closeOnClickOutside: true,
        afterClose: () => cleanupConfirmAlertArtifacts(),
        customUI: (dialogProps) => (
            <ConfirmDeleteUI
                {...dialogProps}
                title={resolvedTitle}
                message={resolvedMessage}
                confirmLabel={resolvedConfirmLabel}
                cancelLabel={resolvedCancelLabel}
                danger={danger}
                busy={busy}
                onConfirm={onConfirm}
                badgeLabel={resolvedBadgeLabel}
                closeLabel={resolvedCloseLabel}
                hintText={resolvedHintText}
                workingLabel={resolvedWorkingLabel}
            />
        ),
    });
}
